/* Havenwell — product hero behaviour: gallery, variant matching, sticky add-to-cart. */

(function () {
  'use strict';

  /* ---------------------------------------------------------------- gallery */

  class HwGallery extends HTMLElement {
    connectedCallback() {
      this.slidesEl = this.querySelector('[data-hw-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-hw-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-hw-thumb]'));
      if (!this.slidesEl || this.slides.length === 0) return;

      this.index = 0;

      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.hwThumb)));
      });

      const prev = this.querySelector('[data-hw-prev]');
      const next = this.querySelector('[data-hw-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.index - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.index + 1));

      // Keep thumbnails in sync when the customer swipes the stage directly.
      this.slidesEl.addEventListener('scroll', () => {
        window.clearTimeout(this.scrollTimer);
        this.scrollTimer = window.setTimeout(() => {
          const width = this.slidesEl.clientWidth;
          if (width === 0) return;
          this.setActive(Math.round(this.slidesEl.scrollLeft / width));
        }, 90);
      });
    }

    goTo(index) {
      const total = this.slides.length;
      const target = ((index % total) + total) % total;
      this.slidesEl.scrollTo({ left: this.slidesEl.clientWidth * target, behavior: 'smooth' });
      this.setActive(target);
    }

    /* Jump to the first media belonging to a variant, used when colour changes. */
    showMedia(mediaId) {
      if (!mediaId) return;
      const match = this.slides.findIndex((slide) => slide.dataset.hwMediaId === String(mediaId));
      if (match > -1) this.goTo(match);
    }

    setActive(index) {
      if (index < 0 || index >= this.slides.length) return;
      this.index = index;
      this.thumbs.forEach((thumb) => {
        thumb.setAttribute('aria-current', thumb.dataset.hwThumb === String(index) ? 'true' : 'false');
      });
    }
  }

  customElements.define('hw-gallery', HwGallery);

  /* ---------------------------------------------------------------- variants */

  class HwProduct extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-hw-variants]');
      if (!data) return;

      try {
        this.variants = JSON.parse(data.textContent);
      } catch (error) {
        console.error('Havenwell: could not parse variant data', error);
        return;
      }

      this.form = this.querySelector('form[data-hw-form]');
      this.idInput = this.querySelector('[data-hw-variant-id]');
      this.button = this.querySelector('[data-hw-atc]');
      this.buttonText = this.querySelector('[data-hw-atc-text]');
      this.priceNow = this.querySelector('[data-hw-price-now]');
      this.priceWas = this.querySelector('[data-hw-price-was]');
      this.priceSave = this.querySelector('[data-hw-price-save]');
      this.gallery = this.querySelector('hw-gallery');
      this.optionLabels = Array.from(this.querySelectorAll('[data-hw-option-value]'));

      /* The sticky bar is rendered outside this element (it is position:fixed),
         so its selects have to be pulled into scope explicitly or they would
         never drive variant selection. */
      this.stickyBar = document.querySelector('hw-sticky-atc');
      const scopes = [this];
      if (this.stickyBar && !this.contains(this.stickyBar)) scopes.push(this.stickyBar);
      const collect = (selector) =>
        scopes.reduce((all, el) => all.concat(Array.from(el.querySelectorAll(selector))), []);

      this.inputs = collect('[data-hw-option-index]');
      this.inputs.forEach((input) => {
        input.addEventListener('change', () => this.onChange());
      });

      /* Quantity bundles: the tier card decides how many units go in the cart. */
      this.qtyField = this.querySelector('[name="quantity"]');
      this.qtyTiers = collect('[data-hw-qty-tier]');
      this.qtyTiers.forEach((tier) => {
        tier.addEventListener('change', () => this.applyQtyTier());
      });

      this.qtySelects = collect('[data-hw-qty-select]');
      this.qtySelects.forEach((select) => {
        select.addEventListener('change', () => {
          const match = this.qtyTiers.find((tier) => tier.dataset.hwQtyTier === select.value);
          if (match) match.checked = true;
          this.applyQtyTier();
        });
      });

      this.applyQtyTier();

      this.onChange({ silent: true });
    }

    applyQtyTier() {
      if (this.qtyTiers.length === 0) return;
      const checked = this.qtyTiers.find((tier) => tier.checked);
      if (!checked) return;

      const quantity = checked.dataset.hwQtyTier;
      if (this.qtyField) this.qtyField.value = quantity;

      // Keep the sticky bar's bundle dropdown showing the same tier.
      this.qtySelects.forEach((select) => {
        if (select.value !== quantity) select.value = quantity;
      });
    }

    selectedOptions() {
      const values = [];

      this.inputs.forEach((input) => {
        // Radios only count when checked. Selects nested inside a tier card only
        // count when that tier is the selected one — the others are hidden.
        if (input.tagName === 'SELECT') {
          const tier = input.closest('.hw-tier');
          const radio = tier ? tier.querySelector('input[type="radio"]') : null;
          if (tier && radio && !radio.checked) return;
          values[Number(input.dataset.hwOptionIndex)] = input.value;
        } else if (input.checked) {
          values[Number(input.dataset.hwOptionIndex)] = input.value;
        }
      });

      return values;
    }

    /* Keep every hidden tier's colour dropdown on the same value, so switching
       bundle size never silently changes the colour the customer picked. */
    syncNestedSelects(selected) {
      this.inputs.forEach((input) => {
        if (input.tagName !== 'SELECT') return;
        const index = Number(input.dataset.hwOptionIndex);
        if (selected[index] && input.value !== selected[index]) input.value = selected[index];
      });
    }

    onChange(options) {
      const selected = this.selectedOptions();
      const variant = this.variants.find((candidate) =>
        candidate.options.every((value, index) => value === selected[index])
      );

      this.reflectOptionNames(selected);
      this.syncNestedSelects(selected);
      this.current = variant;

      if (!variant) {
        this.setUnavailable('Unavailable');
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;

      if (!variant.available) {
        this.setUnavailable('Sold out');
      } else {
        this.setAvailable();
      }

      this.renderPrice(variant);

      if (!options || !options.silent) {
        if (this.gallery && variant.featured_media) this.gallery.showMedia(variant.featured_media.id);
        // Keep the URL shareable/back-button friendly, same behaviour as Dawn.
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.dispatchEvent(new CustomEvent('hw:variant', { bubbles: true, detail: { variant: variant } }));
    }

    reflectOptionNames(selected) {
      this.optionLabels.forEach((el) => {
        const index = Number(el.dataset.hwOptionValue);
        el.textContent = selected[index] || '';
      });
    }

    setUnavailable(label) {
      if (!this.button) return;
      this.button.setAttribute('disabled', 'disabled');
      if (this.buttonText) this.buttonText.textContent = label;
    }

    setAvailable() {
      if (!this.button) return;
      this.button.removeAttribute('disabled');
      if (this.buttonText) this.buttonText.textContent = this.button.dataset.hwAtcLabel || 'Add to cart';
    }

    renderPrice(variant) {
      if (this.priceNow) this.priceNow.textContent = HwProduct.money(variant.price);

      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;

      // Mirror the same figures into the sticky bar.
      const stickyPct = onSale
        ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)
        : 0;

      document.querySelectorAll('[data-hw-sticky-price]').forEach((el) => {
        el.textContent = HwProduct.money(variant.price);
      });

      document.querySelectorAll('[data-hw-sticky-was]').forEach((el) => {
        el.textContent = onSale ? HwProduct.money(variant.compare_at_price) : '';
        el.hidden = !onSale;
      });

      document.querySelectorAll('[data-hw-sticky-save]').forEach((el) => {
        el.textContent = onSale ? 'SAVE ' + stickyPct + '%' : '';
        el.hidden = !onSale;
      });

      if (this.priceWas) {
        this.priceWas.textContent = onSale ? HwProduct.money(variant.compare_at_price) : '';
        this.priceWas.hidden = !onSale;
      }

      if (this.priceSave) {
        if (onSale) {
          const percent = Math.round(
            ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
          );
          this.priceSave.textContent =
            'Save ' + HwProduct.money(variant.compare_at_price - variant.price) + ' (' + percent + '%)';
          this.priceSave.hidden = false;
        } else {
          this.priceSave.hidden = true;
        }
      }
    }

    /* Shopify prices arrive in cents. Falls back gracefully if the shop format is missing. */
    static money(cents) {
      const format = window.hwMoneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const withCommas = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return format.replace(/\{\{\s*amount\s*\}\}/, withCommas);
    }
  }

  customElements.define('hw-product', HwProduct);

  /* ------------------------------------------------------- sticky add to cart */

  class HwStickyAtc extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-hw-sticky-button]');
      this.priceEl = this.querySelector('[data-hw-sticky-price]');

      const anchorId = this.dataset.hwAnchor;
      this.anchor = anchorId ? document.getElementById(anchorId) : null;
      this.product = document.querySelector('hw-product');

      if (this.button && this.product) {
        this.button.addEventListener('click', () => {
          const realButton = this.product.querySelector('[data-hw-atc]');
          if (realButton && !realButton.hasAttribute('disabled')) realButton.click();
        });
      }

      if (this.product && this.priceEl) {
        this.product.addEventListener('hw:variant', (event) => {
          this.priceEl.textContent = HwProduct.money(event.detail.variant.price);
        });
      }

      // Show the bar only once the real buy box has scrolled out of view.
      if (this.anchor && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              this.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
            });
          },
          { threshold: 0 }
        );
        this.observer.observe(this.anchor);
      } else {
        this.classList.add('is-visible');
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  customElements.define('hw-sticky-atc', HwStickyAtc);

  /* ------------------------------------------------------------- countdown */

  class HwCountdown extends HTMLElement {
    connectedCallback() {
      this.hoursEl = this.querySelector('[data-hw-hours]');
      this.minsEl = this.querySelector('[data-hw-mins]');
      this.secsEl = this.querySelector('[data-hw-secs]');

      const windowHours = Number(this.dataset.hwHours) || 8;
      this.spanMs = windowHours * 60 * 60 * 1000;

      // Anchor the deadline to a rolling window so the timer never sits at zero.
      // Stored per browser, so a returning visitor keeps their own countdown.
      const key = 'hw-countdown-' + (this.dataset.hwKey || 'default');
      let deadline = Number(window.localStorage.getItem(key));

      if (!deadline || Number.isNaN(deadline) || deadline < Date.now()) {
        deadline = Date.now() + this.spanMs;
        try {
          window.localStorage.setItem(key, String(deadline));
        } catch (error) {
          /* private mode — the timer still runs, it just resets on reload */
        }
      }

      this.deadline = deadline;
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      let remaining = Math.max(0, this.deadline - Date.now());

      if (remaining === 0) {
        this.deadline = Date.now() + this.spanMs;
        remaining = this.spanMs;
      }

      const totalSeconds = Math.floor(remaining / 1000);
      const pad = (n) => String(n).padStart(2, '0');

      if (this.hoursEl) this.hoursEl.textContent = pad(Math.floor(totalSeconds / 3600));
      if (this.minsEl) this.minsEl.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
      if (this.secsEl) this.secsEl.textContent = pad(totalSeconds % 60);
    }
  }

  customElements.define('hw-countdown', HwCountdown);

  /* ------------------------------------------------- mini review carousel */

  class HwMiniReviews extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hw-mini-track]');
      this.slides = Array.from(this.querySelectorAll('[data-hw-mini-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-hw-mini-dot]'));
      if (!this.track || this.slides.length < 2) return;

      this.index = 0;
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));

      const delay = Number(this.dataset.hwAutoplay) || 0;
      if (delay > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.timer = window.setInterval(() => this.goTo(this.index + 1), delay);
        this.addEventListener('pointerenter', () => window.clearInterval(this.timer));
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    goTo(index) {
      const total = this.slides.length;
      this.index = ((index % total) + total) % total;
      this.track.style.transform = 'translateX(-' + this.index * 100 + '%)';
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    }
  }

  customElements.define('hw-mini-reviews', HwMiniReviews);

  /* -------------------------------------------------------- quantity stepper */

  class HwQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-hw-qty-input]');
      this.minus = this.querySelector('[data-hw-qty-minus]');
      this.plus = this.querySelector('[data-hw-qty-plus]');
      if (!this.input) return;

      this.min = Number(this.input.min) || 1;
      this.max = Number(this.input.max) || Infinity;

      if (this.minus) this.minus.addEventListener('click', () => this.step(-1));
      if (this.plus) this.plus.addEventListener('click', () => this.step(1));

      /* Strip anything that is not a whole number as it is typed. Without this
         a value like "1,5" reaches the browser's own validation, which shows an
         untranslatable native tooltip in the visitor's browser language. */
      this.input.addEventListener('input', () => {
        const cleaned = this.input.value.replace(/[.,].*$/, '').replace(/[^\d]/g, '');
        if (cleaned !== this.input.value) this.input.value = cleaned;
      });

      this.input.addEventListener('change', () => this.clamp());
      this.clamp();
    }

    step(delta) {
      this.input.value = String((Number(this.input.value) || this.min) + delta);
      this.clamp();
    }

    clamp() {
      let value = Number(this.input.value);
      if (Number.isNaN(value)) value = this.min;
      value = Math.min(this.max, Math.max(this.min, Math.round(value)));
      this.input.value = String(value);
      if (this.minus) this.minus.disabled = value <= this.min;
      if (this.plus) this.plus.disabled = value >= this.max;
    }
  }

  customElements.define('hw-qty', HwQty);
})();
